/**
 * Right rail for the weekly order view: read-only month calendar (selected
 * week banded, with the compact order-deadline indicator inside it beneath the
 * legend) → cart. On desktop the rail is pinned to the day-card column's
 * height (see `height`); the calendar keeps its natural height and the cart
 * hugs its contents, capped at the remaining space.
 */

import { cn } from '@ordo/ui/cn'
import type { ReactElement } from 'react'

import type { AdminSchool } from '@/api/school-orders'
import { schoolDeadlinePolicy, weekDeadlineStatus } from '@/lib/school-orders/deadline'

import { CartPanel } from './cart-panel'
import { OrderDeadlineAlert } from './order-deadline-alert'
import { WeekMonthCalendar } from './week-month-calendar'

export interface RightRailProps {
  school: AdminSchool
  /** All the admin's campuses — the cart totals span every campus's lines. */
  schools?: AdminSchool[]
  /** Mon–Fri keys of the selected week. */
  weekKeys: string[]
  /** Dates (`YYYY-MM-DD`) with at least one submitted order — shown green. */
  orderedDates: ReadonlySet<string>
  /** Dates the school marked NO_SERVICE — struck through and inert (PROD-1228). */
  noServiceDates?: ReadonlySet<string>
  /**
   * Pin the rail to this px height (the day-card column's content height) so
   * the cart bottom-aligns with the last day card. `null` → fill the column.
   */
  height?: number | null
  onSelectWeek: (mondayKey: string) => void
  /** View-only surfaces (cafeteria lookahead) keep the calendar, drop the cart. */
  hideCart?: boolean
  /** The surface can't order (read-only, lookahead): no order-deadline countdown. */
  hideDeadline?: boolean
}

export function RightRail({
  school,
  schools,
  weekKeys,
  orderedDates,
  noServiceDates,
  height,
  onSelectWeek,
  hideCart = false,
  hideDeadline = false,
}: RightRailProps): ReactElement {
  const weekMonday = weekKeys[0] ?? ''
  const policy = schoolDeadlinePolicy(school)
  const isWeekClosed = (mondayKey: string): boolean => weekDeadlineStatus(policy, mondayKey).closed
  // Only mount the in-calendar deadline footer when there's a provisioned
  // policy — otherwise the alert renders null and we'd draw an empty divider.
  const hasDeadline = !hideDeadline && weekDeadlineStatus(policy, weekMonday).daysRemaining !== null
  return (
    <aside
      className={cn('flex min-h-0 flex-col gap-4 lg:max-h-full', height == null && 'flex-1')}
      style={height == null ? undefined : { height }}>
      <div className="shrink-0">
        <WeekMonthCalendar
          anchorKey={weekMonday}
          selectedWeekKeys={weekKeys}
          orderedDates={orderedDates}
          noServiceDates={noServiceDates}
          isWeekClosed={isWeekClosed}
          onSelectWeek={onSelectWeek}
          footer={hasDeadline ? <OrderDeadlineAlert policy={policy} weekMondayKey={weekMonday} compact /> : undefined}
        />
      </div>
      {hideCart ? null : <CartPanel school={school} schools={schools} className="min-h-0" />}
    </aside>
  )
}
